import { useState, useEffect } from 'react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ChevronRight, ChevronDown, ChevronLeft } from 'lucide-react';
import categoriesData from '../../data/categories.json';
import productsData from '../../data/products.json';

interface Product {
  id: number;
  name: string;
  partNo?: string;
  category: string;
  brand?: string;
  categoryName?: string;
  price: string;
  image: string;
  description: string;
  fullDescription?: string;
  specifications?: Record<string, string>;
  features?: string[];
}

interface Category {
  id: string;
  name: string;
  url: string;
  parentId?: string;
  type?: string;
}

interface ProductProps {
  initialCategory?: string;
  initialBrand?: string;
  onNavigate: (page: string, params?: any) => void;
}

const ITEMS_PER_PAGE = 12;
const categories: Category[] = categoriesData;
const products: Product[] = productsData;

export function Product({ initialCategory, initialBrand, onNavigate }: ProductProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategory || null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(initialBrand || null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (initialCategory) {
      setSelectedCategory(initialCategory);
      setExpandedCategories(new Set([initialCategory]));
    }
    if (initialBrand) {
      setSelectedBrand(initialBrand);
    }
  }, [initialCategory, initialBrand]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedBrand]);

  // 获取第一级分类（品类）
  const mainCategories = categories.filter(cat => cat.type === 'category' || (!cat.parentId && cat.id !== 'all'));
  
  // 获取第二级分类（品牌）
  const brands = categories.filter(cat => cat.type === 'brand' || (cat.parentId && cat.parentId !== undefined));

  // 获取当前选中品类下的品牌
  const getBrandsForCategory = (categoryId: string) => {
    return brands.filter(brand => brand.parentId === categoryId);
  };

  // 过滤产品：根据品类和品牌
  const filteredProducts = (() => {
    if (!selectedCategory && !selectedBrand) {
      return products; // 显示所有产品
    }
    
    if (selectedBrand) {
      // 如果选择了品牌，只显示该品牌的产品
      return products.filter(
        (product) => product.brand === selectedBrand
      );
    }
    
    if (selectedCategory) {
      // 如果只选择了品类，显示该品类下的所有产品（所有品牌）
      return products.filter(
        (product) => product.category === selectedCategory
      );
    }
    
    return products;
  })();

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedBrand(null); // 清空品牌选择
    // 切换展开状态
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
    onNavigate('Product', { category: categoryId });
  };

  const handleBrandClick = (brandId: string, categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedBrand(brandId);
    onNavigate('Product', { category: categoryId, brand: brandId });
  };

  const handleAllProductsClick = () => {
    setSelectedCategory(null);
    setSelectedBrand(null);
    onNavigate('Product');
  };

  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl mb-8">Our Products</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Categories */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-2xl mb-4">Categories</h2>
              
              {/* All Products */}
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={handleAllProductsClick}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      !selectedCategory && !selectedBrand
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    All Products
                  </button>
                </li>

                {/* 第一级分类（品类） */}
                {mainCategories.map((category) => {
                  const categoryBrands = getBrandsForCategory(category.id);
                  const isExpanded = expandedCategories.has(category.id);
                  const isSelected = selectedCategory === category.id && !selectedBrand;
                  
                  return (
                    <li key={category.id}>
                      <button
                        onClick={() => handleCategoryClick(category.id)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <span>{category.name}</span>
                        {categoryBrands.length > 0 && (
                          <span>
                            {isExpanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </span>
                        )}
                      </button>
                      
                      {/* 第二级分类（品牌） */}
                      {isExpanded && categoryBrands.length > 0 && (
                        <ul className="mt-1 ml-4 space-y-1">
                          <li>
                            <button
                              onClick={() => handleCategoryClick(category.id)}
                              className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                                isSelected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-gray-50 text-gray-600'
                              }`}
                            >
                              All {category.name}
                            </button>
                          </li>
                          {categoryBrands.map((brand) => {
                            const isBrandSelected = selectedBrand === brand.id;
                            return (
                              <li key={brand.id}>
                                <button
                                  onClick={() => handleBrandClick(brand.id, category.id)}
                                  className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                                    isBrandSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : 'hover:bg-gray-50 text-gray-600'
                                  }`}
                                >
                                  {brand.name}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Right Side - Product Grid */}
          <main className="flex-1">
            <div className="mb-6">
              <div className="text-gray-600 mb-2">
                Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredProducts.length)} of {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
              </div>
              {(selectedCategory || selectedBrand) && (
                <div className="text-sm text-gray-500">
                  {selectedBrand 
                    ? `Filtered by: ${brands.find(b => b.id === selectedBrand)?.name || selectedBrand}`
                    : selectedCategory
                    ? `Filtered by: ${mainCategories.find(c => c.id === selectedCategory)?.name || selectedCategory}`
                    : ''
                  }
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {paginatedProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => onNavigate('ProductDetail', { productId: product.id })}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                >
                  <div className="relative h-64 bg-gray-50 flex items-center justify-center">
                    <ImageWithFallback
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-4">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl text-primary">{product.price}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded-lg transition-colors ${
                          currentPage === page
                            ? 'bg-primary text-primary-foreground'
                            : 'border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return <span key={page} className="px-2">...</span>;
                  }
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}